import { Paper, TextField } from '@mui/material';
import { useMemo, useState } from 'react';

import { useStoreInventories } from '@gozoki/api';
import { includesLowerCase } from '@gozoki/tools';
import { GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import { ROUTE_BUILDER } from '../../utils/constants/routes.constants';
import { FourDatePicker } from '../../utils/dashboard/fourDatePicker';
import { timeRanges } from '../../utils/dashboard/timeRanges';
import { getInventoryStatusLabel } from '../../utils/types/toFrench';

const inventoryColumnsDef: GridColDef[] = [
    {
        field: 'label',
        headerName: 'Nom',
        flex: 1,
    },
    {
        field: 'status',
        headerName: 'Statut',
        flex: 1,
        valueFormatter: ({ value }) => getInventoryStatusLabel(value),
    },
    {
        field: 'createdAt',
        headerName: 'Créé le',
        flex: 1,
        valueFormatter: ({ value }) => {
            return new Date(value).toLocaleDateString();
        },
    },
    {
        field: 'validatedAt',
        headerName: 'validé le',
        flex: 1,
        valueFormatter: ({ value }) => {
            return new Date(value).toLocaleDateString();
        },
    },
];

interface PastInventoriesTabProps {
    shopId: number;
}

export const PastInventoriesTab = (props: PastInventoriesTabProps) => {
    const { shopId } = props;
    const [currentTimeRange, setCurrentTimeRange] = useState<number>(-1);
    const [currentDateStart, setCurrentDateStart] = useState<Date>(new Date());
    const [currentDateEnd, setCurrentDateEnd] = useState<Date>(new Date());
    const [inventorySearch, setInventorySearch] = useState<string>('');
    const { data: inventories, isLoading } = useStoreInventories(shopId);
    const navigate = useNavigate();
    const navigateInventoryDetails = (id: number) => navigate(ROUTE_BUILDER.inventoryDetails(id));
    const dateState = {
        timeRanges: {
            all: timeRanges,
            current: currentTimeRange,
            set: setCurrentTimeRange,
        },
        currentDate: {
            start: currentDateStart,
            setStart: setCurrentDateStart,
            end: currentDateEnd,
            setEnd: setCurrentDateEnd,
        },
    };

    const inventoryRows = useMemo(() => {
        if (inventories === undefined) {
            return [];
        }
        // Filter only validated inventories (exclude pending)
        return inventories.filter((inventory) => {
            const creationDate = new Date(inventory.createdAt);
            return (
                inventory.status === 'validated' &&
                includesLowerCase(inventory.label, inventorySearch) &&
                creationDate.getTime() >= currentDateStart.getTime()
            );
        });
    }, [inventories, inventorySearch, currentDateStart]);

    return (
        <Paper sx={{ paddingTop: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'row', margin: 20 }}>
                <FourDatePicker dateState={dateState} />
            </div>
            <>
                <div style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
                    <TextField
                        variant="outlined"
                        label="Recherche"
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                    />
                    <div style={{ flexGrow: 1 }} />
                </div>
                <SimpleDataGrid
                    columns={inventoryColumnsDef}
                    rows={inventoryRows}
                    loading={isLoading}
                    onRowClick={(row) => navigateInventoryDetails(row.row.id)}
                    rowSelection={false}
                    disableRowSelectionOnClick
                    checkboxSelection={false}
                />
            </>
        </Paper>
    );
};
